/**
 * TabRendererInit - Automatically initializes tab rendering when the document loads
 *
 * This module sets up:
 * 1. Initial rendering of all existing tabs when the lease is loaded and layout is stable
 * 2. A MutationObserver to automatically render new tabs as they're added
 *
 * We defer the initial render until "stable" (fonts ready + short delay) so tab width
 * measurements are accurate and don't change after the first click or later layout.
 *
 * @requires TabRenderer
 * @requires TabstopService
 */
angular.module("LeasePilot").run([
  "$timeout",
  "TabRenderer",
  "TabstopService",
  function($timeout, TabRenderer, TabstopService) {
    // Track the observer so we can disconnect if needed
    let observer = null;

    /**
     * Waits until the skeleton loader has been removed from the body element.
     * Falls back to a timeout so we don't block indefinitely if the class
     * is never removed for some reason.
     */
    function waitForSkeletonLoaderRemoval() {
      return new Promise(function(resolve) {
        const body = document.body;

        if (!body) {
          $timeout(resolve, 0);
          return;
        }

        if (!body.classList.contains("skeleton-loader")) {
          resolve();
          return;
        }

        const observer = new MutationObserver(function() {
          if (!body.classList.contains("skeleton-loader")) {
            observer.disconnect();
            resolve();
          }
        });

        observer.observe(body, { attributes: true, attributeFilter: ["class"] });

        // Safety timeout in case the class is never removed
        $timeout(function() {
          observer.disconnect();
          resolve();
        }, 5000);
      });
    }

    /**
     * Waits until the lease layout has a stable width (no more zoom/scale changes).
     * Uses requestAnimationFrame to compare widths across frames instead of
     * arbitrary timeouts.
     */
    function waitForStableLayout() {
      return new Promise(function(resolve) {
        const container =
          document.querySelector(".lease.lease--editor") ||
          document.querySelector(".lease.lease--original");

        if (!container || typeof window.requestAnimationFrame !== "function") {
          resolve();
          return;
        }

        let lastWidth = container.getBoundingClientRect().width;
        let stableFrames = 0;
        let totalFrames = 0;
        const requiredStableFrames = 3;
        const maxFrames = 30; // ~0.5s at 60fps worst-case
        const epsilon = 0.5; // px

        function check() {
          totalFrames += 1;
          const currentWidth = container.getBoundingClientRect().width;

          if (Math.abs(currentWidth - lastWidth) <= epsilon) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
            lastWidth = currentWidth;
          }

          if (stableFrames >= requiredStableFrames || totalFrames >= maxFrames) {
            resolve();
          } else {
            window.requestAnimationFrame(check);
          }
        }

        window.requestAnimationFrame(check);
      });
    }

    /**
     * Schedules initial tab render after layout is stable:
     * - Fonts loaded
     * - Skeleton loader removed
     * - Lease container width stable across frames
     *
     * Avoids wrong tab widths from measuring too early.
     */
    function scheduleInitialRenderWhenStable(leaseOriginal) {
      const renderWhenStable = function() {
        const renderedCount = TabRenderer.renderAllTabs(leaseOriginal);
        if (renderedCount > 0) {
          console.log(
            "TabRenderer: Rendered",
            renderedCount,
            "tabs (after stable layout)"
          );
        }
      };

      // Wait for fonts to load so layout is accurate (document.fonts.ready is a Promise)
      const fontsReadyPromise =
        document.fonts && document.fonts.ready
          ? document.fonts.ready
          : Promise.resolve();

      fontsReadyPromise
        .catch(function() {
          // If fonts fail to load, continue with best-effort layout
          return null;
        })
        .then(function() {
          return waitForSkeletonLoaderRemoval();
        })
        .then(function() {
          return waitForStableLayout();
        })
        .then(function() {
          // Run in Angular's digest but without adding extra random delay
          $timeout(function() {
            renderWhenStable();
          }, 0);
        })
        .catch(function() {
          // Final safety fallback
          $timeout(renderWhenStable, 0);
        });
    }

    /**
     * Initializes tab rendering for the lease document.
     * Sets up the observer immediately; runs initial render when the lease is stable.
     */
    function initTabRendering() {
      const leaseOriginal = document.querySelector(".lease.lease--original");

      if (!leaseOriginal) {
        $timeout(initTabRendering, 500);
        return;
      }

      // Observer first so new tabs (e.g. inserted by user) are still rendered
      observer = TabRenderer.observeChanges(leaseOriginal, {
        debounce: 100
      });
      console.log(
        "TabRenderer: Observer initialized for automatic tab rendering"
      );

      // Defer initial render until layout is stable
      scheduleInitialRenderWhenStable(leaseOriginal);
    }

    /**
     * Ensures newly created tab elements have the correct attributes
     * This is called by ContentEditableManager when inserting tabs
     */
    function ensureTabElement(element) {
      if (!element) {
        return null;
      }

      // Ensure it has the correct class
      if (!element.classList.contains(TabstopService.TAB_CLASS)) {
        element.classList.add(TabstopService.TAB_CLASS);
      }

      // Ensure correct attributes
      TabRenderer.ensureTabAttributes(element);

      // Render the tab (calculate width)
      TabRenderer.renderTab(element);

      return element;
    }

    // Expose initialization function globally for testing/manual triggering
    // This API is used by ContentEditableManager for tab key insertion
    window.tabRendererInit = {
      init: initTabRendering,
      ensureTabElement: ensureTabElement,
      renderTab: function(element) {
        return TabRenderer.renderTab(element);
      },
      renderTabsInParagraph: function(paragraph) {
        return TabRenderer.renderTabsInParagraph(paragraph);
      },
      renderAllTabs: function(container) {
        return TabRenderer.renderAllTabs(container);
      },
      createTabElement: function() {
        const element = TabstopService.createTabElement();
        TabRenderer.ensureTabAttributes(element);
        return element;
      }
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function() {
        // Wait a bit for Angular to finish compiling
        $timeout(initTabRendering, 500);
      });
    } else {
      // DOM already loaded, wait for Angular to finish
      $timeout(initTabRendering, 500);
    }
  }
]);
